/**
 * Painel lateral sobreposto (spec 018): os "Detalhes" do atendimento e as ações
 * que não precisam ficar na tela o tempo todo. Mesma primitiva do Dialog
 * (radix) — foco preso, Esc fecha, o conteúdo por trás continua MONTADO (o
 * rascunho do compositor não se perde ao abrir o painel).
 */
import * as React from 'react';
import { XIcon } from 'lucide-react';
import { Dialog as SheetPrimitive } from 'radix-ui';
import { cn } from '@/lib/utils';

export function Sheet({
  open,
  onClose,
  title,
  description,
  children,
  className,
  side = 'right',
}: {
  open: boolean;
  onClose: () => void;
  title: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  side?: 'right' | 'left';
}) {
  return (
    <SheetPrimitive.Root open={open} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <SheetPrimitive.Portal>
        <SheetPrimitive.Overlay className="fixed inset-0 z-50 bg-black/30 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0" />
        <SheetPrimitive.Content
          data-slot="sheet"
          className={cn(
            'fixed inset-y-0 z-50 flex w-full max-w-[min(34rem,100vw)] flex-col border bg-background shadow-xl outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0',
            side === 'right'
              ? 'right-0 data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right'
              : 'left-0 data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left',
            className
          )}
        >
          <div className="flex shrink-0 items-start justify-between gap-3 border-b px-5 py-3.5">
            <div className="min-w-0">
              <SheetPrimitive.Title className="truncate text-base font-semibold">{title}</SheetPrimitive.Title>
              {description ? (
                <SheetPrimitive.Description className="mt-0.5 text-xs text-muted-foreground">
                  {description}
                </SheetPrimitive.Description>
              ) : (
                <SheetPrimitive.Description className="sr-only">{String(title)}</SheetPrimitive.Description>
              )}
            </div>
            <SheetPrimitive.Close
              className="rounded-sm p-1 text-muted-foreground opacity-80 transition hover:bg-muted hover:opacity-100 focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:outline-none"
              aria-label="Fechar"
            >
              <XIcon className="size-4" />
            </SheetPrimitive.Close>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>
        </SheetPrimitive.Content>
      </SheetPrimitive.Portal>
    </SheetPrimitive.Root>
  );
}
